import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * Generic scroll-reveal host.
 *
 * Adds `is-visible` to itself the first time it scrolls into view, letting CSS
 * run the entrance transitions. Brand-neutral and reusable by any section/block.
 *
 * Contract:
 * - No-JS  → host never upgrades, so the server-rendered FINAL state is shown.
 * - JS     → adds `reveal-ready` on connect (CSS pre-state hides/offsets the
 *            children instantly, no transition), then `is-visible` on intersect
 *            to animate to the final state.
 * - Reduced motion → jumps straight to `is-visible`, skipping the pre-state.
 *
 * Optional data-attributes:
 * - `data-reveal-threshold` (0–1, default 0)
 * - `data-reveal-margin`    (IntersectionObserver rootMargin, default "0px 0px -10% 0px")
 *
 * @extends Component
 */
class RevealOnScroll extends Component {
  /** @type {IntersectionObserver | undefined} */
  #observer;
  #hasRun = false;

  connectedCallback() {
    super.connectedCallback();

    // Reduced motion: skip the entrance, show the final state immediately.
    if (prefersReducedMotion()) {
      this.classList.add('is-visible');
      return;
    }

    // Enable the CSS pre-state (children hidden/offset) only now that JS is live.
    this.classList.add('reveal-ready');
    this.#observe();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
  }

  #observe() {
    // Fallback: no IntersectionObserver → reveal right away so nothing stays hidden.
    if (typeof IntersectionObserver === 'undefined') {
      this.#run();
      return;
    }

    const threshold = parseFloat(this.dataset.revealThreshold) || 0;
    const rootMargin = this.dataset.revealMargin || '0px 0px -10% 0px';

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.#run();
        }
      },
      { threshold, rootMargin }
    );
    this.#observer.observe(this);
  }

  #run() {
    if (this.#hasRun) return;
    this.#hasRun = true;
    this.#observer?.disconnect();
    // Swap the pre-state class out so the (less specific) `is-visible` rules win
    // and the entrance transitions run from the pre-state to the final state.
    this.classList.remove('reveal-ready');
    this.classList.add('is-visible');
  }
}

if (!customElements.get('reveal-on-scroll')) {
  customElements.define('reveal-on-scroll', RevealOnScroll);
}
